import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StreamerCard from "@/components/feature/StreamerCard";
import type { Streamer } from "@/types/content";
import { streamers } from "@/mocks/streamers";
import FilterBar from "@/pages/category/components/FilterBar";

const allStreamers = streamers as Streamer[];

const TYPE_OPTIONS = ["全部", "新闻主播", "娱乐主播", "综艺主播"];
const YEAR_OPTIONS = ["全部", "2014-2016", "2017-2019", "2020以后"];

const uniqueRegions = Array.from(new Set(allStreamers.map((s) => s.region)));
const REGION_OPTIONS = ["全部", ...uniqueRegions];

const PAGE_SIZE = 8;

function matchYear(year: number, range: string) {
  if (range === "全部") return true;
  if (range === "2014-2016") return year >= 2014 && year <= 2016;
  if (range === "2017-2019") return year >= 2017 && year <= 2019;
  if (range === "2020以后") return year >= 2020;
  return true;
}

export default function Category() {
  const [searchParams] = useSearchParams();

  const [keyword, setKeyword] = useState(searchParams.get("q") || "");
  const [type, setType] = useState(searchParams.get("type") || "全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState(searchParams.get("sort") || "latest");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setKeyword(searchParams.get("q") || "");
    setType(searchParams.get("type") || "全部");
    setSort(searchParams.get("sort") || "latest");
  }, [searchParams]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [keyword, type, region, year, sort]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const result = allStreamers.filter((s) => {
      const matchKeyword =
        !kw ||
        s.name.toLowerCase().includes(kw) ||
        s.nameEn.toLowerCase().includes(kw) ||
        s.program.toLowerCase().includes(kw) ||
        s.tags.some((t) => t.toLowerCase().includes(kw));
      const matchType = type === "全部" || s.type === type;
      const matchRegion = region === "全部" || s.region === region;
      const matchYearRange = matchYear(s.debutYear, year);
      return matchKeyword && matchType && matchRegion && matchYearRange;
    });
    return result.sort((a, b) =>
      sort === "hot" ? b.heat - a.heat : b.updated.localeCompare(a.updated)
    );
  }, [keyword, type, region, year, sort]);

  const visible = filtered.slice(0, visibleCount);
  const activeFilters = [type, region, year].filter((v) => v !== "全部").length;

  const handleReset = () => {
    setKeyword("");
    setType("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
  };

  return (
    <>
      <Seo
        title="韩国女主播分类列表 – 韩国女主播"
        description="按主播类型、地区与活跃年份筛选韩国女主播，支持关键词搜索与最新、最热排序，快速定位你感兴趣的主播并查看详细资料与动态。"
        keywords="韩国女主播分类,韩国女主播列表,新闻主播,娱乐主播,综艺主播,主播筛选"
        canonical="/category"
      />

      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "女主播分类" }]} />

        <header className="mt-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">女主播分类列表</h1>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">
            汇集新闻、娱乐、综艺等不同类型韩国女主播，可按地区、活跃年份多维筛选，帮助你快速定位感兴趣的主播并查看其资料、新闻与作品。
          </p>
        </header>

        <FilterBar
          keyword={keyword}
          onKeyword={setKeyword}
          type={type}
          onType={setType}
          region={region}
          onRegion={setRegion}
          year={year}
          onYear={setYear}
          sort={sort}
          onSort={setSort}
          typeOptions={TYPE_OPTIONS}
          regionOptions={REGION_OPTIONS}
          yearOptions={YEAR_OPTIONS}
          onReset={handleReset}
        />

        <div className="flex items-center justify-between mt-6 mb-4">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-semibold text-accent-600">{filtered.length}</span> 位主播
            {activeFilters > 0 && <span className="text-foreground-400">（已应用 {activeFilters} 个筛选条件）</span>}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="font-heading font-semibold text-foreground-900 mb-2">没有找到匹配的主播</h3>
            <p className="text-sm text-foreground-500 mb-5">试试更换关键词或放宽筛选条件</p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {visible.map((s) => (
                <StreamerCard key={s.id} streamer={s} />
              ))}
            </div>

            {visibleCount < filtered.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {filtered.length - visibleCount} 位）
                </button>
              </div>
            )}

            {visibleCount >= filtered.length && filtered.length > PAGE_SIZE && (
              <p className="text-center text-xs text-foreground-400 mt-8">已经到底啦，共 {filtered.length} 位主播</p>
            )}
          </>
        )}
      </div>
    </>
  );
}